import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import type { ChangeEvent, MouseEvent, ReactNode } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useSWRConfig } from 'swr';
import { useRouter, useSearchParams } from '@/lib/router';
import { PageSkeleton } from '@/components/PageSkeleton';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import { BriefcaseIcon, TrendUpIcon, TrendDownIcon, MinusIcon, CaretDownIcon, CaretLeftIcon, CaretRightIcon, ArrowsDownUpIcon, CurrencyDollarIcon, TagIcon, ChartPieSliceIcon, DropIcon, CalendarDotsIcon, MagnifyingGlassIcon, XIcon, InfoIcon, CopySimpleIcon } from '@phosphor-icons/react';
import { SortChips } from '@/components/ui/SortChips';
import Image from '@/components/ui/Image';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { KpiStat } from '@/components/ui/KpiStat';
import { MoneyValue } from '@/components/ui/MoneyValue';
import { RemovableFilterPill } from '@/components/ui/RemovableFilterPill';
import { SurfaceCard } from '@/components/ui/surface';
import { TooltipContent, TooltipRoot, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { getPaginationWindow } from '@/components/transactions/TransactionPagination';
import { revalidateReviewQueues } from '@/hooks/queries/swrConfig';
import { revalidateInvestmentHoldings } from '@/hooks/queries/useInvestmentHoldings';
import { revalidatePostRefundCaches } from '@/hooks/queries/useReviewQueue';
import { InvestmentGroupPickerDrawer } from '@/components/investments/InvestmentGroupPickerDrawer';
import { investmentsApi } from '@/lib/api';
import { InvestmentAllocationChart, type AllocationCategoryData } from './InvestmentAllocationChart';
import { InvestmentsFlowChart } from './InvestmentsFlowChart';
import { InvestmentPortfolioEvolutionChart } from './InvestmentPortfolioEvolutionChart';
import { InvestmentCalendarView } from './InvestmentCalendarView';
import { InvestmentDetailDrawer } from './InvestmentDetailDrawer';
import { InvestmentDuplicateReviewCard } from './InvestmentDuplicateReviewCard';
import { InvestmentSourceCluster, InvestmentSourceList } from './InvestmentSources';
import { InvestmentStatusBadge } from './InvestmentStatusBadge';
import { ErrorState } from './ErrorState';
import { PageHeader } from './PageHeader';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import type {
  InvestmentSummary,
  InvestmentItem,
  InstitutionFacet,
  AllocationBreakdown,
} from '@/types';
import { assetTagToneCls, formatPercent } from '@/lib/investments/transactions';
import {
  LIQUIDITY_AVAILABILITY_FLAG,
  LIQUIDITY_BUCKET_EXPLANATIONS,
  LIQUIDITY_BUCKET_LABELS,
  LEGACY_LIQUIDITY_LABELS,
  LEGACY_LIQUIDITY_SUBS,
  buildLegacyLiquidityProfile,
  buildLiquidityDistributionPayload,
  isAvailabilityBucketKey,
  liquidityFilterLabel,
  matchesLegacyLiquidity,
} from '@/lib/investments/liquidity';
import { useThemeColors } from '@/lib/theme/useThemeColors';
import { token, type ThemePalette } from '@/lib/theme/tokens';
import { track, useFeatureFlags } from '@/lib/telemetry';

const ALLOCATION_CONFIG: Record<keyof AllocationBreakdown, { label: string }> = {
  fixedIncome: { label: 'Renda Fixa' },
  variableIncome: { label: 'Renda Variável' },
  funds: { label: 'Fundos' },
  realEstate: { label: 'Imóveis' },
  other: { label: 'Outros' },
};

// Allocation slices use the categorical --chart-* tokens (§01.6):
// teal fixed income, blue variable income, orange funds, purple real estate.
function allocationColor(key: keyof AllocationBreakdown, theme: ThemePalette): string {
  switch (key) {
    case 'fixedIncome':
      return token('--chart-5', theme.success);
    case 'variableIncome':
      return token('--chart-7', theme.info);
    case 'funds':
      return token('--chart-2', theme.warning);
    case 'realEstate':
      return token('--chart-8', theme.textSecondary);
    default:
      return token('--neutral-400', theme.textTertiary);
  }
}

const SUBTYPE_LABELS: Record<string, string> = {
  TREASURY: 'Tesouro Direto',
  'Tesouro Direto': 'Tesouro Direto',
  CDB: 'CDB',
  LCI: 'LCI',
  LCA: 'LCA',
  CRI: 'CRI',
  CRA: 'CRA',
  LC: 'LC',
  DEBENTURES: 'Debêntures',
  'Debêntures': 'Debêntures',
  CORPORATE_DEBT: 'Dívida Corporativa',
  STOCK: 'Ações',
  'Ações': 'Ações',
  ETF: 'ETF',
  REAL_ESTATE_FUND: 'FII',
  FII: 'FII',
  BDR: 'BDR',
  DERIVATIVES: 'Derivativos',
  OPTION: 'Opções',
  INVESTMENT_FUND: 'Fundo de Investimento',
  MULTIMARKET_FUND: 'Fundo Multimercado',
  FIXED_INCOME_FUND: 'Fundo Renda Fixa',
  STOCK_FUND: 'Fundo de Ações',
  ETF_FUND: 'Fundo ETF',
  OFFSHORE_FUND: 'Fundo Offshore',
  FIP_FUND: 'FIP',
  EXCHANGE_FUND: 'Fundo Cambial',
  FI_INFRA: 'FI-Infra',
  FI_AGRO: 'FI-Agro',
  RETIREMENT: 'Previdência',
  'Previdência': 'Previdência',
  STRUCTURED_NOTE: 'Nota Estruturada',
  OTHER: 'Outros',
  null: 'Sem subtipo',
};

function getSubtypeLabel(subtype?: string | null): string | null {
  if (!subtype) return null;
  return SUBTYPE_LABELS[subtype] ?? subtype;
}

type InvestmentSortField = 'balance' | 'type' | 'profit' | 'name';
type InvestmentSortOrder = 'asc' | 'desc';
type IndexerBucket = { key: string; name: string; value: number; color: string };

const ITEMS_PER_PAGE = 10;

const SORT_FIELDS: readonly InvestmentSortField[] = ['balance', 'type', 'profit', 'name'];
const ALLOCATION_KEYS: readonly string[] = ['fixedIncome', 'variableIncome', 'funds', 'realEstate', 'other'];

// The API refuses a sort or a bucket it doesn't declare: a link from before, or one edited by hand, reads the default.
function sortFieldParam(value: string | null): InvestmentSortField {
  return SORT_FIELDS.find((field) => field === value) ?? 'balance';
}

function sortOrderParam(value: string | null): InvestmentSortOrder {
  return value === 'asc' ? 'asc' : 'desc';
}

function isAllocationKey(value: string): boolean {
  return ALLOCATION_KEYS.includes(value);
}

function getTypeAllocationKey(type: string): keyof AllocationBreakdown {
  const typeMap: Record<string, keyof AllocationBreakdown> = {
    'Renda Fixa': 'fixedIncome',
    'Renda Variavel': 'variableIncome',
    'Renda Variável': 'variableIncome',
    'Fundos': 'funds',
    'ETF': 'variableIncome',
    'Titulos': 'fixedIncome',
    'Títulos': 'fixedIncome',
    'COE': 'other',
    'Outros': 'other',
    'FII': 'realEstate',
    'Fundo Imobiliario': 'realEstate',
    'Fundo Imobiliário': 'realEstate',
    'Real Estate': 'realEstate',
    'Imóveis': 'realEstate',
    'Imoveis': 'realEstate',
  };
  return typeMap[type] || 'other';
}

interface SectionHeadingProps {
  icon?: ReactNode;
  title: string;
  hint?: string;
  count?: number;
}

function SectionHeading({ icon, title, hint, count }: SectionHeadingProps) {
  return (
    <div className="mb-3 flex min-w-0 items-center gap-2">
      {icon && <span className="shrink-0">{icon}</span>}
      <h2 className="min-w-0 truncate text-sm font-semibold text-text-primary">{title}</h2>
      {count != null && (
        <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 font-mono text-2xs text-text-secondary tabular-nums">
          {count}
        </span>
      )}
      {hint && <span className="min-w-0 truncate text-2xs text-text-secondary">{hint}</span>}
    </div>
  );
}

function IndexerBucketRow({
  bucket,
  active,
  dimmed,
  pct,
  onSelect,
}: {
  bucket: IndexerBucket;
  active: boolean;
  dimmed: boolean;
  pct: number;
  onSelect: (indexer: string | null) => void;
}) {
  function handleClick() {
    onSelect(bucket.key);
  }

  return (
    <button
      onClick={handleClick}
      aria-pressed={active}
      className={`w-full rounded-lg px-2 py-1.5 text-left transition-colors ${active ? 'bg-brand-50 ring-1 ring-border-brand' : 'hover:bg-accent'}`}
    >
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className={`min-w-0 truncate text-sm font-semibold sm:text-xs ${active ? 'text-brand-700' : 'text-text-secondary'}`}>
          {bucket.name}
        </span>
        <span className={`shrink-0 font-mono text-sm font-bold tabular-nums sm:text-xs ${active ? 'text-brand-700' : 'text-text-primary'}`}>
          {pct.toFixed(0)}%
        </span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full transition-[width]"
          style={{ width: `${pct}%`, backgroundColor: bucket.color, opacity: dimmed ? 0.3 : 1 }}
        />
      </div>
    </button>
  );
}

interface IndexerConcentrationCardProps {
  data: IndexerBucket[];
  total: number;
  selectedIndexer: string | null;
  onSelect: (indexer: string | null) => void;
}

function IndexerConcentrationCard({
  data,
  total,
  selectedIndexer,
  onSelect,
}: IndexerConcentrationCardProps) {
  // Mockup-approved form: one horizontal token-colored bar per bucket (no pie).
  return (
    <Card className="flex h-full flex-col p-4" data-testid="investment-indexer-card">
      <SectionHeading
        icon={<TagIcon size={14} className="text-primary" />}
        title="Indexadores · Renda Fixa"
      />
      <div className="mt-1 flex flex-1 flex-col justify-center space-y-2">
        {data.map((bucket) => (
          <IndexerBucketRow
            key={bucket.key}
            bucket={bucket}
            active={selectedIndexer === bucket.key}
            dimmed={selectedIndexer !== null && selectedIndexer !== bucket.key}
            pct={total > 0 ? (bucket.value / total) * 100 : 0}
            onSelect={onSelect}
          />
        ))}
      </div>
    </Card>
  );
}

interface LiquidityDatum {
  key: string;
  name: string;
  sub?: string;
  description?: string;
  value: number;
  color: string;
}

function LiquiditySegment({
  entry,
  widthPct,
  active,
  dimmed,
  title,
  onSelect,
}: {
  entry: LiquidityDatum;
  widthPct: number;
  active: boolean;
  dimmed: boolean;
  title: string;
  onSelect: (key: string | null) => void;
}) {
  function handleClick() {
    onSelect(entry.key);
  }

  // TooltipTrigger goes asChild onto the button itself — a wrapper span would
  // take over the width the flex bar assigns per segment.
  return (
    <TooltipRoot>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={handleClick}
          aria-label={entry.name}
          aria-pressed={active}
          style={{
            width: `${widthPct}%`,
            backgroundColor: entry.color,
            opacity: dimmed ? 0.3 : 1,
          }}
          // Inset outline: the parent bar clips with overflow-hidden, so an outside
          // ring would be invisible to keyboard users.
          className="h-full transition-opacity hover:opacity-80 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring"
        />
      </TooltipTrigger>
      <TooltipContent>
        {title}
        {entry.description && (
          <span className="mt-1 block max-w-56 text-2xs opacity-80">{entry.description}</span>
        )}
      </TooltipContent>
    </TooltipRoot>
  );
}

function LiquidityBar({
  liquidityData,
  total,
  selectedLiquidity,
  onSelect,
  formatCurrency,
}: {
  liquidityData: LiquidityDatum[];
  total: number;
  selectedLiquidity: string | null;
  onSelect: (key: string | null) => void;
  formatCurrency: (n: number) => string;
}) {
  return (
    <div
      className="flex h-4 w-full overflow-hidden rounded-full bg-surface-sunken ring-1 ring-border-subtle"
      role="group"
      aria-label="Distribuição por liquidez"
    >
      {liquidityData.map((entry) => {
        const widthPct = total > 0 ? (entry.value / total) * 100 : 0;
        if (widthPct <= 0) return null;
        return (
          <LiquiditySegment
            key={entry.key}
            entry={entry}
            widthPct={widthPct}
            active={selectedLiquidity === entry.key}
            dimmed={selectedLiquidity !== null && selectedLiquidity !== entry.key}
            title={`${entry.name}${entry.sub ? ` · ${entry.sub}` : ''}: ${formatCurrency(entry.value)} (${widthPct.toFixed(1)}%)`}
            onSelect={onSelect}
          />
        );
      })}
    </div>
  );
}

function LiquidityLegendRow({
  datum,
  active,
  dimmed,
  pct,
  valueLabel,
  onSelect,
}: {
  datum: LiquidityDatum;
  active: boolean;
  dimmed: boolean;
  pct: number;
  valueLabel?: string;
  onSelect: (key: string | null) => void;
}) {
  function handleClick() {
    onSelect(datum.key);
  }

  return (
    <button
      onClick={handleClick}
      aria-pressed={active}
      className={`flex w-full items-center justify-between rounded-lg px-2 py-1.5 transition-colors ${active ? 'bg-brand-50 ring-1 ring-border-brand' : 'hover:bg-accent'}`}
    >
      <div className="flex min-w-0 items-center gap-1.5">
        <div className="size-2.5 shrink-0 rounded-full" style={{ backgroundColor: datum.color, opacity: dimmed ? 0.3 : 1 }} />
        <div className="flex min-w-0 flex-col items-start">
          <span className={`min-w-0 truncate text-sm font-semibold sm:text-xs ${active ? 'text-brand-700' : 'text-text-secondary'}`}>{datum.name}</span>
          {datum.sub && <span className="text-2xs text-text-tertiary">{datum.sub}</span>}
        </div>
      </div>
      {/* §02 numeral hierarchy: the amount is the primary numeral, the % secondary;
          the fixed-width % keeps columns aligned across 6%–100%. Legacy rows (no
          valueLabel) keep the original styling. */}
      {valueLabel ? (
        <span className="flex shrink-0 items-baseline gap-2">
          <span className={`font-mono text-sm font-semibold tabular-nums sm:text-xs ${active ? 'text-brand-700' : 'text-text-primary'}`}>
            <MoneyValue text={valueLabel} />
          </span>
          <span className={`w-10 shrink-0 text-right font-mono text-2xs tabular-nums ${active ? 'text-brand-700' : 'text-text-tertiary'}`}>{pct.toFixed(0)}%</span>
        </span>
      ) : (
        <span className={`shrink-0 font-mono text-sm font-bold tabular-nums sm:text-xs ${active ? 'text-brand-700' : 'text-text-primary'}`}>{pct.toFixed(0)}%</span>
      )}
    </button>
  );
}

function LiquidityMethodologyPopover() {
  const enabled = useFeatureFlags(LIQUIDITY_AVAILABILITY_FLAG);
  if (!enabled) return null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Como classificamos a liquidez"
          className="shrink-0 cursor-pointer rounded-md p-1 text-text-tertiary transition-colors hover:bg-accent hover:text-text-secondary"
        >
          <InfoIcon size={14} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 text-sm">
        <p className="font-semibold text-text-primary">Como classificamos</p>
        <ul className="mt-2 list-disc space-y-1 pl-4 text-xs text-text-secondary">
          <li>Renda fixa bancária (CDB, LCI…): pela carência informada pelo banco — cumprida, o dinheiro conta como disponível.</li>
          <li>Tesouro Direto, ações, ETFs e FIIs: liquidez diária (recompra garantida ou negociação em bolsa).</li>
          <li>COE: só no vencimento.</li>
          <li>Fundos e previdência: pelo valor disponível para resgate informado — o prazo de liquidação (D+N) não é exposto pelo Open Finance.</li>
          <li>Sem nenhuma dessas informações, o ativo fica como Indeterminado — nunca chutamos.</li>
        </ul>
        <p className="mt-2 text-xs text-text-tertiary">Valores brutos: resgates podem ter desconto de IR/IOF.</p>
      </PopoverContent>
    </Popover>
  );
}

function LiquiditySettlementNote({ count }: { count: number }) {
  const enabled = useFeatureFlags(LIQUIDITY_AVAILABILITY_FLAG);
  if (!enabled || count <= 0) return null;

  return (
    <p className="mt-3 text-2xs text-text-tertiary">
      Inclui fundos/previdência — prazo de liquidação (D+N) não informado pelo Open Finance; confira a política de resgate.
    </p>
  );
}

function LiquidityFlagBadges({ inv }: { inv: InvestmentItem }) {
  const enabled = useFeatureFlags(LIQUIDITY_AVAILABILITY_FLAG);
  const formatCurrency = useFormatCurrency();

  if (!enabled) return null;
  // A redeemed position's signals are stale — availability caveats would be noise.
  if (inv.status === 'TOTAL_WITHDRAWAL') return null;
  if (!inv.blockedContradiction && !inv.partialAvailability && !inv.settlementUnknown) return null;

  return (
    <>
      {inv.blockedContradiction && (
        <Badge variant="warning" size="sm">Resgate bloqueado pelo banco</Badge>
      )}
      {inv.partialAvailability && inv.amountWithdrawal != null && (
        <Badge variant="info" size="sm" className="normal-case tabular-nums">
          Disponível: {formatCurrency(inv.amountWithdrawal)} de {formatCurrency(inv.balance)}
        </Badge>
      )}
      {inv.settlementUnknown && (
        <Badge variant="outline" size="sm" className="normal-case">Prazo de liquidação não informado</Badge>
      )}
    </>
  );
}

type Trend = 'positive' | 'negative' | 'neutral';

function trendOf(value: number): Trend {
  if (value > 0) return 'positive';
  if (value < 0) return 'negative';
  return 'neutral';
}

// Directional KPI tiles map the value's sign onto KpiStat's semantic tones.
const KPI_TREND_TONE: Record<Trend, 'emerald' | 'rose' | 'neutral'> = {
  positive: 'emerald',
  negative: 'rose',
  neutral: 'neutral',
};

function trendIconOf(trend: Trend) {
  if (trend === 'positive') return TrendUpIcon;
  if (trend === 'negative') return TrendDownIcon;
  return MinusIcon;
}

interface MaturityItemProps {
  inv: InvestmentItem & { dueDateObj: Date };
  formatCurrency: (n: number) => string;
}

function MaturityItem({ inv, formatCurrency }: MaturityItemProps) {
  const d = inv.dueDateObj;
  const now = new Date();
  const monthLabel = d
    .toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' })
    .replace('. de ', '/');
  const monthsAway = (d.getFullYear() - now.getFullYear()) * 12 + (d.getMonth() - now.getMonth());
  const relative =
    monthsAway <= 0
      ? 'este mês'
      : monthsAway === 1
        ? 'em 1 mês'
        : monthsAway < 24
          ? `em ${monthsAway} meses`
          : `em ${Math.round(monthsAway / 12)} anos`;
  return (
    <div className="flex items-center justify-between gap-3 py-3">
      <div className="flex min-w-0 items-center gap-2.5">
        {inv.institutionImage ? (
          <Image src={inv.institutionImage} alt="" width={24} height={24} className="size-6 shrink-0 rounded-full" />
        ) : (
          <span className="grid size-6 shrink-0 place-items-center rounded-full bg-muted text-3xs font-bold text-text-secondary" aria-hidden>
            {(inv.institution ?? inv.name).charAt(0).toUpperCase()}
          </span>
        )}
        <div className="min-w-0">
          <p className="truncate text-sm font-bold text-text-primary">{inv.name}</p>
          <p className="text-2xs text-text-tertiary">{monthLabel} · {relative}</p>
        </div>
      </div>
      <span className="shrink-0 font-mono text-sm font-bold text-text-primary tabular-nums">
        <MoneyValue text={formatCurrency(inv.balance)} />
      </span>
    </div>
  );
}

// Fixed-income maturities by year — a magnitude chart, so bars sit on a single
// family (info) ramp per §01.6; the primary hue is never a data series.
function FixedIncomeMaturityChart({
  data,
  formatCurrency,
}: {
  data: Array<{ year: number; total: number; count: number }>;
  formatCurrency: (n: number, options?: { compact?: boolean }) => string;
}) {
  const maxTotal = Math.max(...data.map((m) => m.total));
  return (
    <>
      <div
        className="mt-4 grid min-h-32 items-end gap-3"
        style={{ gridTemplateColumns: `repeat(${data.length}, minmax(0, 1fr))` }}
      >
        {data.map((m) => {
          const heightPct = maxTotal > 0 ? (m.total / maxTotal) * 80 : 0;
          const isPeak = m.total === maxTotal;
          return (
            <div key={m.year} className="flex h-full min-w-0 flex-col items-center justify-end gap-1">
              <span className={`max-w-full truncate font-mono text-3xs tabular-nums ${isPeak ? 'font-medium text-text-secondary' : 'text-text-tertiary'}`}>
                {formatCurrency(m.total, { compact: true })}
              </span>
              <TooltipRoot>
                <TooltipTrigger asChild>
                  <div
                    className={`w-full max-w-14 rounded-t ${isPeak ? 'bg-info' : 'bg-info/30'}`}
                    style={{ height: `${Math.max(heightPct, 6)}%` }}
                  />
                </TooltipTrigger>
                <TooltipContent>{`${m.year} · ${m.count} ativo(s) · ${formatCurrency(m.total)}`}</TooltipContent>
              </TooltipRoot>
            </div>
          );
        })}
      </div>
      <div
        className="mt-2 grid gap-3 text-center font-mono text-3xs text-text-tertiary tabular-nums"
        style={{ gridTemplateColumns: `repeat(${data.length}, minmax(0, 1fr))` }}
      >
        {data.map((m) => (
          <span key={m.year}>{m.year}</span>
        ))}
      </div>
    </>
  );
}

function InstitutionChip({
  facet,
  active,
  onSelect,
}: {
  facet: InstitutionFacet;
  active: boolean;
  onSelect: (name: string | null) => void;
}) {
  function handleClick() {
    onSelect(facet.name);
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-pressed={active}
      className={`flex h-9 shrink-0 items-center gap-1.5 rounded-lg py-0 pr-3 pl-1.5 text-sm font-bold transition-colors sm:h-8 sm:text-xs ${
        active
          ? 'bg-primary text-primary-foreground'
          : 'bg-surface-sunken text-text-secondary hover:bg-accent active:bg-accent'
      }`}
    >
      {facet.imageUrl ? (
        <Image src={facet.imageUrl} alt="" width={20} height={20} className="size-5 shrink-0 rounded-full" />
      ) : (
        <span className="grid size-5 shrink-0 place-items-center rounded-full bg-muted text-3xs font-bold text-text-secondary" aria-hidden>
          {facet.name.charAt(0).toUpperCase()}
        </span>
      )}
      <span className="max-w-32 truncate">{facet.name}</span>
      <span className="font-mono text-3xs tabular-nums opacity-60">{facet.count}</span>
    </button>
  );
}

function PageNumberButton({
  page,
  isActive,
  onSelect,
}: {
  page: number;
  isActive: boolean;
  onSelect: (page: number) => void;
}) {
  function handleClick() {
    onSelect(page);
  }

  return (
    <button
      onClick={handleClick}
      className={`min-w-8 h-8 rounded-lg text-sm font-bold transition-colors ${isActive ? 'bg-primary text-primary-foreground' : 'text-text-secondary hover:bg-accent'}`}
    >
      {page}
    </button>
  );
}

function SortableHeader({
  field,
  label,
  widthClassName,
  sortField,
  sortOrder,
  onSort,
}: {
  field: InvestmentSortField;
  label: string;
  widthClassName: string;
  sortField: InvestmentSortField;
  sortOrder: InvestmentSortOrder;
  onSort: (field: InvestmentSortField) => void;
}) {
  const isActive = sortField === field;

  function handleClick() {
    onSort(field);
  }

  return (
    <th
      className={`${widthClassName} px-3 py-2.5 text-right whitespace-nowrap`}
      aria-sort={isActive ? (sortOrder === 'desc' ? 'descending' : 'ascending') : 'none'}
    >
      <button onClick={handleClick} className="ml-auto flex items-center gap-1 transition-colors hover:text-text-secondary">
        {label}
        {isActive && <ArrowsDownUpIcon size={10} />}
      </button>
    </th>
  );
}

/** Chevron that opens a grouped row's per-connection breakdown. Kept out of the row's own
 * click target — expanding must not open the detail drawer. */
function SourcesToggle({
  expanded,
  name,
  onToggle,
}: {
  expanded: boolean;
  name: string;
  onToggle: () => void;
}) {
  function handleClick(event: MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    onToggle();
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-expanded={expanded}
      aria-label={`Instituições de ${name}`}
      className="shrink-0 cursor-pointer rounded p-0.5 text-text-tertiary transition-colors hover:bg-accent hover:text-text-secondary"
    >
      <CaretDownIcon size={12} className={expanded ? 'rotate-180 transition-transform' : 'transition-transform'} />
    </button>
  );
}

function InvestmentTableRow({
  inv,
  isFiltered,
  formatCurrency,
  onOpenDetail,
  onOpenGrouping,
}: {
  inv: InvestmentItem;
  isFiltered: boolean;
  formatCurrency: (n: number) => string;
  onOpenDetail: (id: string) => void;
  onOpenGrouping: (id: string) => void;
}) {
  const allocationKey = (inv.subtype === 'FII' ? 'realEstate' : getTypeAllocationKey(inv.type)) as keyof typeof ALLOCATION_CONFIG;
  const config = ALLOCATION_CONFIG[allocationKey];
  const isWithdrawn = inv.status === 'TOTAL_WITHDRAWAL';
  const sources = inv.sources ?? [];
  const isGrouped = sources.length > 1;
  const [sourcesExpanded, setSourcesExpanded] = useState(false);

  function handleOpen() {
    onOpenDetail(inv.id);
  }

  function handleNameClick(event: MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    onOpenDetail(inv.id);
  }

  function handleGroupClick(event: MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    onOpenGrouping(inv.id);
  }

  function handleToggleSources() {
    setSourcesExpanded((current) => !current);
  }

  return (
    <>
      <tr className={`cursor-pointer border-b border-border-subtle transition-colors ${isWithdrawn ? 'opacity-60' : ''} ${isFiltered ? 'bg-brand-50/40 hover:bg-brand-50/70' : 'hover:bg-accent/50'}`} onClick={handleOpen}>
        <td className="max-w-0 py-3 pr-4 pl-0">
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="flex min-w-0 items-center gap-1.5">
              {/* A real button so opening the holding is keyboard-reachable — the row's own click
                  handler is a mouse convenience the keyboard can't use. */}
              <button
                onClick={handleNameClick}
                className="cursor-pointer truncate text-left text-sm font-bold text-text-primary hover:text-brand-700"
                aria-label={`Ver detalhes de ${inv.name}`}
              >
                {inv.name}
              </button>
              <InvestmentStatusBadge status={inv.status} />
              <InvestmentSourceCluster sources={sources} />
              {isGrouped && (
                <SourcesToggle expanded={sourcesExpanded} name={inv.name} onToggle={handleToggleSources} />
              )}
            </span>
            {(getSubtypeLabel(inv.subtype) || inv.issuer) && (
              <span className="truncate text-2xs text-text-tertiary">
                {[getSubtypeLabel(inv.subtype), inv.issuer].filter(Boolean).join(' · ')}
              </span>
            )}
            <span className="flex flex-wrap gap-1 empty:hidden">
              <LiquidityFlagBadges inv={inv} />
            </span>
          </div>
        </td>
        <td className="hidden p-3 lg:table-cell">
          <div className="flex items-center gap-2">
            {inv.institutionImage && (
              <Image src={inv.institutionImage} alt="" width={16} height={16} className="shrink-0 rounded" />
            )}
            <span className="max-w-30 truncate text-sm text-text-secondary">{inv.institution || '—'}</span>
          </div>
        </td>
        <td className="p-3">
          <span className={`inline-block rounded-md px-2 py-1 font-mono text-3xs font-semibold tracking-wider uppercase ${assetTagToneCls(allocationKey)}`}>
            {config?.label || inv.type}
          </span>
        </td>
        <td className="hidden p-3 text-right font-mono text-sm font-medium text-text-secondary tabular-nums lg:table-cell">
          {inv.amountOriginal != null ? formatCurrency(inv.amountOriginal) : '—'}
        </td>
        <td className="p-3 text-right font-mono text-sm font-bold text-text-primary tabular-nums">
          {formatCurrency(inv.balance)}
        </td>
        <td className={`p-3 text-right font-mono text-sm font-bold tabular-nums ${inv.profitPercent == null ? 'text-text-tertiary' : inv.profitPercent >= 0 ? 'text-amount-positive' : 'text-amount-negative'}`}>
          {inv.profitPercent != null ? formatPercent(inv.profitPercent) : '—'}
        </td>
        <td className="py-3 pr-0 pl-3">
          <div className="flex items-center justify-end">
            {/* Only ungrouped rows: merging an already-grouped row through the single-role picker
                would re-role its sources. Its members are already one holding. */}
            {!isWithdrawn && !inv.groupId && (
              <button
                onClick={handleGroupClick}
                className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-border bg-surface px-2.5 py-1 text-2xs font-medium whitespace-nowrap text-text-primary transition-colors hover:bg-surface-sunken"
                aria-label={`Agrupar ${inv.name} com outra posição`}
              >
                <CopySimpleIcon className="size-3" weight="bold" />
                Agrupar
              </button>
            )}
          </div>
        </td>
      </tr>
      {isGrouped && sourcesExpanded && (
        <tr className="border-b border-border-subtle bg-surface-sunken/50">
          <td colSpan={7} className="px-0 py-3">
            <InvestmentSourceList sources={sources} />
          </td>
        </tr>
      )}
    </>
  );
}

function InvestmentMobileCard({
  inv,
  formatCurrency,
  onOpenDetail,
  onOpenGrouping,
}: {
  inv: InvestmentItem;
  formatCurrency: (n: number) => string;
  onOpenDetail: (id: string) => void;
  onOpenGrouping: (id: string) => void;
}) {
  const allocationKey = inv.subtype === 'FII' ? 'realEstate' : getTypeAllocationKey(inv.type);
  const config = ALLOCATION_CONFIG[allocationKey];
  const isWithdrawn = inv.status === 'TOTAL_WITHDRAWAL';
  const sources = inv.sources ?? [];
  const isGrouped = sources.length > 1;
  const [sourcesExpanded, setSourcesExpanded] = useState(false);

  function handleOpen() {
    onOpenDetail(inv.id);
  }

  function handleDetailsClick(event: MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    onOpenDetail(inv.id);
  }

  function handleGroupClick(event: MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    onOpenGrouping(inv.id);
  }

  function handleToggleSources() {
    setSourcesExpanded((current) => !current);
  }

  return (
    <Card className={`cursor-pointer p-4 ${isWithdrawn ? 'opacity-60' : ''}`} onClick={handleOpen}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="flex min-w-0 items-center gap-1.5">
            <span className="truncate text-base font-bold text-text-primary">{inv.name}</span>
            <InvestmentSourceCluster sources={sources} />
            {isGrouped && (
              <SourcesToggle expanded={sourcesExpanded} name={inv.name} onToggle={handleToggleSources} />
            )}
          </p>
          {(getSubtypeLabel(inv.subtype) || inv.issuer || inv.institution) && (
            <p className="truncate text-sm text-text-tertiary">
              {[getSubtypeLabel(inv.subtype), inv.issuer || inv.institution].filter(Boolean).join(' · ')}
            </p>
          )}
          <span className="mt-1 flex flex-wrap items-center gap-1.5">
            <span className={`inline-block rounded-md px-2 py-1 font-mono text-3xs font-semibold tracking-wider uppercase ${assetTagToneCls(allocationKey)}`}>
              {config?.label ?? inv.type}
            </span>
            <InvestmentStatusBadge status={inv.status} />
            <LiquidityFlagBadges inv={inv} />
          </span>
        </div>
        <div className="shrink-0 text-right">
          <p className="font-mono text-base font-bold text-text-primary tabular-nums">{formatCurrency(inv.balance)}</p>
          {inv.profitPercent != null && (
            <p className={`text-sm font-bold font-mono tabular-nums ${inv.profitPercent >= 0 ? 'text-amount-positive' : 'text-amount-negative'}`}>
              {formatPercent(inv.profitPercent)}
            </p>
          )}
        </div>
      </div>
      {isGrouped && sourcesExpanded && (
        <div className="mt-3 rounded-xl bg-surface-sunken p-3">
          <InvestmentSourceList sources={sources} />
        </div>
      )}
      <div className="mt-3 flex items-center gap-2">
        {/* Ungrouped rows only — an already-grouped row's members are one holding already. */}
        {!isWithdrawn && !inv.groupId && (
          <button
            onClick={handleGroupClick}
            className="inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-xl border border-border bg-surface px-3 py-2 text-sm font-medium text-text-primary transition-colors active:bg-surface-sunken"
            aria-label={`Agrupar ${inv.name} com outra posição`}
          >
            <CopySimpleIcon className="size-4" weight="bold" />
            Agrupar
          </button>
        )}
        <button
          onClick={handleDetailsClick}
          className="inline-flex w-full items-center justify-center gap-1.5 rounded-xl bg-muted px-3 py-2 text-sm font-bold text-text-secondary transition-colors active:bg-brand-50 active:text-brand-700"
        >
          Detalhes
          <CaretRightIcon size={12} />
        </button>
      </div>
    </Card>
  );
}

// Remounted per account: after an in-place session switch, nothing read for the previous account
// (summary, positions, filters) can render for the next one, not even for a frame.
export function InvestmentView() {
  const { userId } = useAuth();
  return <AccountInvestmentView key={userId ?? ''} />;
}

function AccountInvestmentView() {
  const { getToken } = useAuth();
  const formatCurrency = useFormatCurrency();
  const router = useRouter();
  const searchParams = useSearchParams();
  const theme = useThemeColors();
  const liquidityLensEnabled = useFeatureFlags(LIQUIDITY_AVAILABILITY_FLAG);

  const sortField = sortFieldParam(searchParams.get('sortField'));
  const sortOrder = sortOrderParam(searchParams.get('sortOrder'));
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const categoryParams = searchParams.getAll('category').filter(isAllocationKey);
  const isCategoryNegated = searchParams.get('exclude') === 'true';

  const categoryParamsKey = categoryParams.join(',');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const selectedCategories = useMemo(() => new Set(categoryParams), [categoryParamsKey]);

  const institutionParams = searchParams.getAll('institution');
  // JSON, not join(',') — institution names are free text and may themselves contain commas.
  const institutionParamsKey = JSON.stringify(institutionParams);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const selectedInstitutions = useMemo(() => new Set(institutionParams), [institutionParamsKey]);

  const [summary, setSummary] = useState<InvestmentSummary | null>(null);
  const [positions, setPositions] = useState<InvestmentItem[]>([]);
  const [positionsTotal, setPositionsTotal] = useState(0);
  const [institutionFacets, setInstitutionFacets] = useState<InstitutionFacet[]>([]);
  const [detailInvestmentId, setDetailInvestmentId] = useState<string | null>(null);
  const [isPositionsLoading, setIsPositionsLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [retryKey, setRetryKey] = useState(0);
  // The retry key the summary was last read for. A retry or a portfolio change bumps the key, so
  // the next read carries a fresh summary; filter, sort and page changes read the positions alone.
  const summaryKeyRef = useRef<number | null>(null);

  const updateParams = useCallback((updates: Record<string, string | string[] | null>, useReplace = true) => {
    const params = new URLSearchParams(searchParams.toString());

    Object.entries(updates).forEach(([key, value]) => {
      if (value === null) {
        params.delete(key);
      } else if (Array.isArray(value)) {
        params.delete(key);
        value.forEach(v => params.append(key, v));
      } else {
        params.set(key, value);
      }
    });

    const url = `/investments?${params.toString()}`;
    if (useReplace) {
      router.replace(url, { scroll: false });
    } else {
      router.push(url, { scroll: false });
    }
  }, [router, searchParams]);

  const handleSort = useCallback((field: InvestmentSortField) => {
    track('ui.filter_applied', {
      surface: 'investments',
      filter: 'sort',
      action: 'set',
      method: 'control',
      value: field,
    });
    if (sortField === field) {
      updateParams({
        sortOrder: sortOrder === 'desc' ? 'asc' : 'desc',
        page: '1'
      });
    } else {
      updateParams({
        sortField: field,
        sortOrder: 'desc',
        page: '1'
      });
    }
  }, [sortField, sortOrder, updateParams]);

  const handleCategorySelect = useCallback((categoryKey: string | null) => {
    // Clearing with nothing selected is a no-op — don't report a clear.
    if (categoryKey !== null || selectedCategories.size > 0) {
      track('ui.filter_applied', {
        surface: 'investments',
        filter: 'category',
        action: categoryKey === null || selectedCategories.has(categoryKey) ? 'clear' : 'set',
        method: 'chart',
      });
    }
    if (categoryKey === null) {
      updateParams({ category: null, exclude: null, page: '1' });
      return;
    }

    const newCategories = new Set(selectedCategories);
    if (newCategories.has(categoryKey)) {
      newCategories.delete(categoryKey);
    } else {
      newCategories.add(categoryKey);
    }

    if (newCategories.size === 0) {
      updateParams({ category: null, exclude: null, page: '1' });
    } else {
      updateParams({
        category: Array.from(newCategories),
        page: '1'
      });
    }
  }, [selectedCategories, updateParams]);

  const handleInstitutionSelect = useCallback((name: string | null) => {
    // "Todas" with nothing selected is a no-op — don't report a clear.
    if (name !== null || selectedInstitutions.size > 0) {
      track('ui.filter_applied', {
        surface: 'investments',
        filter: 'institution',
        action: name === null || selectedInstitutions.has(name) ? 'clear' : 'set',
        method: 'chip',
      });
    }
    if (name === null) {
      updateParams({ institution: null, page: '1' });
      return;
    }

    const next = new Set(selectedInstitutions);
    if (next.has(name)) {
      next.delete(name);
    } else {
      next.add(name);
    }

    updateParams({ institution: next.size > 0 ? Array.from(next) : null, page: '1' });
  }, [selectedInstitutions, updateParams]);

  const handleInstitutionClearAll = useCallback(() => {
    handleInstitutionSelect(null);
  }, [handleInstitutionSelect]);

  const handleToggleFilterMode = useCallback(() => {
    if (selectedCategories.size > 0) {
      track('ui.filter_applied', {
        surface: 'investments',
        filter: 'category_mode',
        action: 'set',
        method: 'control',
        value: isCategoryNegated ? 'include' : 'exclude',
      });
      updateParams({ exclude: isCategoryNegated ? null : 'true', page: '1' });
    }
  }, [selectedCategories.size, isCategoryNegated, updateParams]);

  const handlePageChange = useCallback((page: number) => {
    updateParams({ page: String(page) });
  }, [updateParams]);

  const allocationChartData: AllocationCategoryData[] = useMemo(() => {
    if (!summary) return [];
    return Object.entries(summary.allocation)
      .filter(([, item]) => item.value > 0)
      .map(([key, item]) => ({
        key,
        label: ALLOCATION_CONFIG[key as keyof AllocationBreakdown]?.label || key,
        value: item.value,
        percentage: item.percentage,
        count: item.count,
        color: allocationColor(key as keyof AllocationBreakdown, theme),
      }))
      .sort((a, b) => b.value - a.value);
  }, [summary, theme]);

  const filteredTotals = useMemo(() => {
    if (!summary) return { totalBalance: 0, totalProfit: 0, count: 0 };
    return {
      totalBalance: summary.totalBalance,
      totalProfit: summary.totalProfit,
      count: positionsTotal,
    };
  }, [summary, positionsTotal]);

  const [selectedIndexer, setSelectedIndexer] = useState<string | null>(null);
  const [selectedLiquidity, setSelectedLiquidity] = useState<string | null>(null);

  // The feature flag can resolve after the user already clicked a segment, and the two
  // widget generations use disjoint filter keys — a cross-generation selection would
  // silently empty the list, so it is cleared instead.
  useEffect(() => {
    if (selectedLiquidity && liquidityLensEnabled !== isAvailabilityBucketKey(selectedLiquidity)) {
      setSelectedLiquidity(null);
    }
  }, [liquidityLensEnabled, selectedLiquidity]);

  const filteredInvestments = useMemo(() => {
    let result = positions;

    if (selectedCategories.size > 0) {
      result = result.filter(inv => {
        const bucket = inv.subtype === 'FII' ? 'realEstate' : getTypeAllocationKey(inv.type);
        const matchesType = selectedCategories.has(bucket);
        return isCategoryNegated ? !matchesType : matchesType;
      });
    }

    if (selectedIndexer) {
      result = result.filter(inv => {
        const rt = inv.rateType?.toUpperCase() ?? '';
        if (selectedIndexer === 'CDI') return rt.includes('CDI');
        if (selectedIndexer === 'IPCA') return rt.includes('IPCA');
        if (selectedIndexer === 'PRE') return rt === 'FIXED' || rt === 'PRE' || rt === 'PREFIXADO';
        if (selectedIndexer === 'OTHER') return !rt.includes('CDI') && !rt.includes('IPCA') && rt !== 'FIXED' && rt !== 'PRE' && rt !== 'PREFIXADO';
        return true;
      });
    }

    if (selectedLiquidity) {
      // Lens on: the same server-resolved bucket the widget counted, so this pre-filter
      // always agrees with the server-side `liquidity` param. Lens off: the original
      // dueDate semantics, matching the legacy keys the widget emits.
      result = liquidityLensEnabled
        ? result.filter(inv => inv.liquidityBucket === selectedLiquidity)
        : result.filter(inv => matchesLegacyLiquidity(inv, selectedLiquidity));
    }

    return result;
  }, [positions, selectedCategories, isCategoryNegated, selectedIndexer, selectedLiquidity, liquidityLensEnabled]);

  const [searchQuery, setSearchQuery] = useState('');
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [includeWithdrawn, setIncludeWithdrawn] = useState(false);

  useEffect(() => {
    let alive = true;
    const withSummary = summaryKeyRef.current !== retryKey;
    if (withSummary) {
      setIsLoading(true);
      setError(null);
    }

    setIsPositionsLoading(true);
    getToken()
      .then((token) => {
        if (!token) throw new Error('Not authenticated');
        return investmentsApi.getAll(token, {
          limit: ITEMS_PER_PAGE,
          offset: (currentPage - 1) * ITEMS_PER_PAGE,
          sortField,
          sortOrder,
          q: searchQuery.trim() || undefined,
          category: selectedCategories.size > 0 ? Array.from(selectedCategories) : undefined,
          exclude: isCategoryNegated || undefined,
          indexer: selectedIndexer ?? undefined,
          liquidity: selectedLiquidity ?? undefined,
          institution: selectedInstitutions.size > 0 ? Array.from(selectedInstitutions) : undefined,
          includeWithdrawn: includeWithdrawn || undefined,
          includeSummary: withSummary || undefined,
        });
      })
      .then((page) => {
        if (!alive) return;
        if (withSummary) {
          summaryKeyRef.current = retryKey;
          setSummary(page.summary ?? null);
        }
        setPositions(page.positions.items);
        setPositionsTotal(page.positions.total);
        setInstitutionFacets(page.institutions);
      })
      .catch((err) => {
        if (withSummary) {
          if (alive) setError(err instanceof Error ? err.message : 'Erro ao carregar investimentos');
          return;
        }
        console.error('Investment positions load failed', err);
        if (alive) {
          setPositions([]);
          setPositionsTotal(0);
        }
      })
      .finally(() => {
        if (!alive) return;
        setIsPositionsLoading(false);
        if (withSummary) setIsLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [
    getToken,
    currentPage,
    sortField,
    sortOrder,
    searchQuery,
    selectedCategories,
    isCategoryNegated,
    selectedIndexer,
    selectedLiquidity,
    selectedInstitutions,
    includeWithdrawn,
    retryKey,
  ]);

  const handleSearchChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    // Empty↔non-empty transitions only (trimmed — whitespace-only is not a
    // search) — never per keystroke, never the text.
    const had = searchQuery.trim() !== '';
    const has = event.target.value.trim() !== '';
    if (!had && has) {
      track('ui.filter_applied', {
        surface: 'investments',
        filter: 'search',
        action: 'set',
        method: 'search',
      });
    } else if (had && !has) {
      track('ui.filter_applied', {
        surface: 'investments',
        filter: 'search',
        action: 'clear',
        method: 'search',
      });
    }
    setSearchQuery(event.target.value);
    if (currentPage !== 1) {
      updateParams({ page: '1' });
    }
  }, [currentPage, searchQuery, updateParams]);

  const handleSearchClear = useCallback(() => {
    if (searchQuery.trim() !== '') {
      track('ui.filter_applied', {
        surface: 'investments',
        filter: 'search',
        action: 'clear',
        method: 'search',
      });
    }
    setSearchQuery('');
    if (currentPage !== 1) {
      updateParams({ page: '1' });
    }
  }, [currentPage, searchQuery, updateParams]);

  const handleIndexerSelect = useCallback((indexer: string | null) => {
    const cleared = indexer === null || selectedIndexer === indexer;
    track('ui.filter_applied', {
      surface: 'investments',
      filter: 'indexer',
      action: cleared ? 'clear' : 'set',
      method: 'chart',
      ...(cleared ? {} : { value: indexer }),
    });
    setSelectedIndexer((current) => current === indexer ? null : indexer);
    if (currentPage !== 1) {
      updateParams({ page: '1' });
    }
  }, [currentPage, selectedIndexer, updateParams]);

  const handleLiquiditySelect = useCallback((liquidity: string | null) => {
    const cleared = liquidity === null || selectedLiquidity === liquidity;
    track('ui.filter_applied', {
      surface: 'investments',
      filter: 'liquidity',
      action: cleared ? 'clear' : 'set',
      method: 'chart',
      ...(cleared ? {} : { value: liquidity }),
    });
    setSelectedLiquidity((current) => current === liquidity ? null : liquidity);
    if (currentPage !== 1) {
      updateParams({ page: '1' });
    }
  }, [currentPage, selectedLiquidity, updateParams]);

  const handleClearInvestmentFilters = useCallback(() => {
    track('ui.filter_applied', {
      surface: 'investments',
      filter: 'all',
      action: 'clear',
      method: 'control',
    });
    setSearchQuery('');
    setSelectedIndexer(null);
    setSelectedLiquidity(null);
    updateParams({ category: null, exclude: null, institution: null, page: '1' });
  }, [updateParams]);

  const handleToggleWithdrawn = useCallback(() => {
    track('ui.filter_applied', {
      surface: 'investments',
      filter: 'include_withdrawn',
      action: includeWithdrawn ? 'clear' : 'set',
      method: 'control',
    });
    setIncludeWithdrawn((current) => !current);
    if (currentPage !== 1) {
      updateParams({ page: '1' });
    }
  }, [currentPage, includeWithdrawn, updateParams]);

  const handleOpenCalendar = useCallback(() => {
    track('action.investment.open_calendar');
    setCalendarOpen(true);
  }, []);

  const handleOpenDetail = useCallback((id: string) => {
    track('action.investment.open_detail', { source: 'list' });
    setDetailInvestmentId(id);
  }, []);

  const handleDetailDrawerOpenChange = useCallback((open: boolean) => {
    if (!open) setDetailInvestmentId(null);
  }, []);

  // The immediate pre-fetch fallback: the drawer shows the grouping from the list row it was
  // opened from until the /investments/{id} detail response arrives (which populates its own
  // sources and then supersedes these props).
  const detailRow = useMemo(
    () => positions.find((inv) => inv.id === detailInvestmentId) ?? null,
    [positions, detailInvestmentId],
  );

  const handleRetry = useCallback(() => {
    setRetryKey((current) => current + 1);
  }, []);

  // Grouping decisions move the portfolio total, so both the summary and the page of
  // positions have to come back from the server.
  const { mutate: mutateGlobal } = useSWRConfig();
  // A grouping decision — from the review card OR a Separar in the drawer — changes the shared
  // review queue (the pair leaves or re-enters it) AND the portfolio total the home widgets
  // read, not only this page's positions.
  const handlePortfolioChanged = useCallback(() => {
    setRetryKey((current) => current + 1);
    revalidateReviewQueues(mutateGlobal);
    revalidatePostRefundCaches(mutateGlobal);
    revalidateInvestmentHoldings(mutateGlobal);
  }, [mutateGlobal]);

  // Manual grouping: the row whose group picker is open (null = closed).
  const [groupingAnchorId, setGroupingAnchorId] = useState<string | null>(null);
  const groupingAnchor = useMemo(
    () => positions.find((inv) => inv.id === groupingAnchorId) ?? null,
    [positions, groupingAnchorId],
  );
  const handleOpenGrouping = useCallback((id: string) => setGroupingAnchorId(id), []);
  const handleCloseGrouping = useCallback(() => setGroupingAnchorId(null), []);

  const searchedInvestments = useMemo(() => {
    if (!searchQuery.trim()) return filteredInvestments;
    const q = searchQuery.toLowerCase();
    return filteredInvestments.filter(
      inv =>
        inv.name.toLowerCase().includes(q) ||
        (inv.issuer?.toLowerCase().includes(q) ?? false) ||
        (inv.sources?.some(source => source.institution?.toLowerCase().includes(q)) ?? false) ||
        inv.type.toLowerCase().includes(q),
    );
  }, [filteredInvestments, searchQuery]);

  const searchedTotalPages = Math.ceil(positionsTotal / ITEMS_PER_PAGE);
  useEffect(() => {
    if (currentPage > searchedTotalPages && searchedTotalPages > 0) {
      updateParams({ page: String(searchedTotalPages) });
    }
  }, [currentPage, searchedTotalPages, updateParams]);

  const handlePrevPage = useCallback(() => {
    handlePageChange(Math.max(1, currentPage - 1));
  }, [handlePageChange, currentPage]);

  const handleNextPage = useCallback(() => {
    handlePageChange(Math.min(searchedTotalPages, currentPage + 1));
  }, [handlePageChange, searchedTotalPages, currentPage]);

  const paginatedSearched = searchedInvestments;

  // The list may opt into showing redeemed history ("Incluir resgatados"), but the
  // portfolio analytics cards (indexers, maturities, liquidity) always describe the
  // live portfolio — a redeemed position's stale balance must not inflate them.
  const activePositions = useMemo(
    () => positions.filter(inv => inv.status !== 'TOTAL_WITHDRAWAL'),
    [positions],
  );

  const fixedIncomeIndexers = useMemo(() => {
    if (!summary) return { CDI: 0, IPCA: 0, PRE: 0, OTHER: 0, total: 0 };
    const fi = activePositions.filter(inv => inv.type === 'Renda Fixa');
    const total = fi.reduce((s, i) => s + i.balance, 0);
    const CDI = fi.filter(inv => inv.rateType?.toUpperCase().includes('CDI')).reduce((s, i) => s + i.balance, 0);
    const IPCA = fi.filter(inv => inv.rateType?.toUpperCase().includes('IPCA')).reduce((s, i) => s + i.balance, 0);
    const PRE = fi.filter(inv => {
      const rt = inv.rateType?.toUpperCase() ?? '';
      return rt === 'FIXED' || rt === 'PRE' || rt === 'PREFIXADO';
    }).reduce((s, i) => s + i.balance, 0);
    const OTHER = Math.max(0, total - CDI - IPCA - PRE);
    return { CDI, IPCA, PRE, OTHER, total };
  }, [summary, activePositions]);

  const indexerData = useMemo<IndexerBucket[]>(
    // Indexer buckets on the categorical --chart-* tokens (§01.6 — never the
    // brand hue): olive CDI, cyan IPCA, purple PRE, neutral Outros.
    () => [
      { key: 'CDI', name: 'CDI', value: fixedIncomeIndexers.CDI, color: token('--chart-3', theme.textSecondary) },
      { key: 'IPCA', name: 'IPCA', value: fixedIncomeIndexers.IPCA, color: token('--chart-6', theme.info) },
      { key: 'PRE', name: 'Prefixado', value: fixedIncomeIndexers.PRE, color: token('--chart-8', theme.textSecondary) },
      { key: 'OTHER', name: 'Outros', value: fixedIncomeIndexers.OTHER, color: token('--neutral-400', theme.textTertiary) },
    ].filter(bucket => bucket.value > 0),
    [fixedIncomeIndexers, theme],
  );

  const hasUsefulIndexerData = useMemo(
    () => indexerData.filter(bucket => bucket.key !== 'OTHER').length >= 2,
    [indexerData],
  );

  useEffect(() => {
    if (!hasUsefulIndexerData && selectedIndexer) {
      setSelectedIndexer(null);
    }
  }, [hasUsefulIndexerData, selectedIndexer]);

  const upcomingMaturities = useMemo(() => {
    if (!summary) return [];
    const today = new Date();
    return activePositions
      .filter(inv => inv.dueDate)
      .map(inv => ({ ...inv, dueDateObj: new Date(inv.dueDate!) }))
      .filter(inv => inv.dueDateObj > today)
      .sort((a, b) => a.dueDateObj.getTime() - b.dueDateObj.getTime())
      .slice(0, 5);
  }, [summary, activePositions]);

  // Mockup-approved granularity: fixed-income maturities bucketed by YEAR, each bar
  // carrying its compact money label (months were unreadable and unlabeled).
  const maturityByYear = useMemo(() => {
    if (!summary) return [];
    const today = new Date();
    const map = new Map<number, { total: number; count: number }>();
    activePositions
      .filter(inv => inv.dueDate)
      .map(inv => ({ ...inv, dueDateObj: new Date(inv.dueDate!) }))
      .filter(inv => inv.dueDateObj > today)
      .forEach(inv => {
        const year = inv.dueDateObj.getFullYear();
        const existing = map.get(year);
        if (existing) {
          existing.total += inv.balance;
          existing.count += 1;
        } else {
          map.set(year, { total: inv.balance, count: 1 });
        }
      });
    return Array.from(map.entries())
      .sort(([a], [b]) => a - b)
      .slice(0, 6)
      .map(([year, bucket]) => ({ year, ...bucket }));
  }, [summary, activePositions]);

  // Ordinal liquidity ladder on the info sequential ramp (§01.6 — one family,
  // darkest = most liquid; semantic amount tokens are reserved for signed money).
  // `theme` is the reactivity trigger: token() reads the DOM, so the memoized
  // palette reference changing on a theme flip is what re-resolves these colors.
  //
  // Lens on: whole-portfolio availability from the summary breakdown — never the
  // paginated list. Lens off: the original page-scoped dueDate histogram.
  const liquidityData = useMemo<LiquidityDatum[]>(() => {
    if (liquidityLensEnabled) {
      if (!summary) return [];
      const buckets = summary.liquidity;
      return [
        { key: 'daily', name: LIQUIDITY_BUCKET_LABELS.daily, description: LIQUIDITY_BUCKET_EXPLANATIONS.daily, value: buckets.daily.value, color: token('--info-700', theme.info) },
        { key: 'short', name: LIQUIDITY_BUCKET_LABELS.short, description: LIQUIDITY_BUCKET_EXPLANATIONS.short, value: buckets.short.value, color: token('--info-500', theme.info) },
        { key: 'long', name: LIQUIDITY_BUCKET_LABELS.long, description: LIQUIDITY_BUCKET_EXPLANATIONS.long, value: buckets.long.value, color: token('--info-300', theme.info) },
        { key: 'unknown', name: LIQUIDITY_BUCKET_LABELS.unknown, description: LIQUIDITY_BUCKET_EXPLANATIONS.unknown, value: buckets.unknown.value, color: token('--neutral-400', theme.textTertiary) },
      ].filter(d => d.value > 0);
    }
    if (!summary) return [];
    const legacy = buildLegacyLiquidityProfile(activePositions);
    return [
      { key: 'immediate', name: LEGACY_LIQUIDITY_LABELS.immediate, sub: LEGACY_LIQUIDITY_SUBS.immediate, value: legacy.immediate, color: token('--info-700', theme.info) },
      { key: 'shortTerm', name: LEGACY_LIQUIDITY_LABELS.shortTerm, sub: LEGACY_LIQUIDITY_SUBS.shortTerm, value: legacy.shortTerm, color: token('--info-500', theme.info) },
      { key: 'longTerm', name: LEGACY_LIQUIDITY_LABELS.longTerm, sub: LEGACY_LIQUIDITY_SUBS.longTerm, value: legacy.longTerm, color: token('--info-300', theme.info) },
    ].filter(d => d.value > 0);
  }, [liquidityLensEnabled, summary, activePositions, theme]);

  const liquidityTotal = useMemo(
    () => liquidityData.reduce((sum, d) => sum + d.value, 0),
    [liquidityData],
  );

  const liquidityDistributionTracked = useRef(false);
  useEffect(() => {
    if (!summary?.liquidity || liquidityDistributionTracked.current) return;
    liquidityDistributionTracked.current = true;
    track('investments.liquidity_distribution', buildLiquidityDistributionPayload(summary.liquidity));
  }, [summary]);

  if (isLoading) {
    return <PageSkeleton />;
  }

  if (error && !summary) {
    return (
      <ErrorState
        title="Erro ao carregar investimentos"
        message="Não conseguimos carregar seus investimentos agora. Verifique sua internet e tente novamente."
        onAction={handleRetry}
      />
    );
  }

  if (!summary) {
    return (
      <div className="flex min-h-96 flex-col items-center justify-center gap-6">
        <div className="rounded-2xl bg-brand-50 p-4">
          <BriefcaseIcon size={48} className="text-primary" />
        </div>
        <div className="text-center">
          <h2 className="text-xl font-bold text-text-primary">Nenhum investimento encontrado</h2>
          <p className="mt-2 max-w-md text-base text-text-secondary sm:text-sm">
            Conecte uma conta de investimentos para visualizar sua carteira.
          </p>
        </div>
      </div>
    );
  }

  const totalProfitPct = summary.totalOriginal > 0
    ? (summary.totalProfit / summary.totalOriginal) * 100
    : 0;
  const profitTrend = trendOf(totalProfitPct);
  const monthlyChangeTrend = trendOf(summary.monthlyChangePercent);
  const minutesSinceUpdate = Math.floor((Date.now() - new Date(summary.lastUpdated).getTime()) / 60000);
  const advancedFilterCount = selectedCategories.size + selectedInstitutions.size + (selectedIndexer ? 1 : 0) + (selectedLiquidity ? 1 : 0);
  const hasSearch = searchQuery.trim().length > 0;
  const hasActiveInvestmentControls = hasSearch || advancedFilterCount > 0;
  const isFiltered = selectedCategories.size > 0 || selectedInstitutions.size > 0 || selectedIndexer !== null || selectedLiquidity !== null;

  const visiblePageNumbers = getPaginationWindow(currentPage, searchedTotalPages);

  return (
    <div className="animate-in space-y-6 pb-24 duration-500 fade-in">
      <PageHeader
        title="Investimentos"
        titleClassName="font-display text-2xl font-semibold tracking-normal sm:text-3xl"
      />

      {/* Pendências first, as on /transactions: a duplicate distorts every figure below it,
          so it is asked about before the numbers are read. Self-guarding — nothing to review
          leaves no trace on the page. */}
      <InvestmentDuplicateReviewCard onResolved={handlePortfolioChanged} />

      {/* KPIs — shared KpiStat tiles, one grid for mobile (2×2) and desktop (1×4) */}
      <section className="grid auto-rows-fr grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4">
        <div className="min-w-0" data-testid="investment-kpi-card">
          <KpiStat
            icon={BriefcaseIcon}
            tone="brand"
            label="Patrimônio Total"
            value={<MoneyValue text={formatCurrency(summary.totalBalance)} />}
          />
        </div>
        <div className="min-w-0" data-testid="investment-kpi-card">
          <KpiStat
            icon={CurrencyDollarIcon}
            tone="neutral"
            label="Valor Investido"
            value={<MoneyValue text={formatCurrency(summary.totalOriginal)} />}
            detail={`Atualizado há ${minutesSinceUpdate} min`}
            hideDetailOnMobile
          />
        </div>
        <div className="min-w-0" data-testid="investment-kpi-card">
          <KpiStat
            icon={trendIconOf(profitTrend)}
            tone={KPI_TREND_TONE[profitTrend]}
            label="Rentabilidade Acumulada"
            value={formatPercent(totalProfitPct)}
            valueFont="sans"
            detail={
              <>
                Lucro: <span className="font-mono tabular-nums">{formatCurrency(summary.totalProfit)}</span>
              </>
            }
          />
        </div>
        <div className="min-w-0" data-testid="investment-kpi-card">
          <KpiStat
            icon={trendIconOf(monthlyChangeTrend)}
            tone={KPI_TREND_TONE[monthlyChangeTrend]}
            label="Variação Mensal"
            value={formatPercent(summary.monthlyChangePercent)}
            valueFont="sans"
            detail={
              <>
                <span className="font-mono tabular-nums">{formatCurrency(summary.monthlyChange)}</span> este mês
              </>
            }
          />
        </div>
      </section>

      {/* Evolution + flow charts */}
      <section className="grid auto-rows-fr grid-cols-1 items-stretch gap-3 lg:grid-cols-2">
        <InvestmentPortfolioEvolutionChart
          evolution={summary.portfolioEvolution}
          trends={summary.monthlyFlow}
          currentBalance={summary.totalBalance}
          currentOriginal={summary.totalOriginal}
          currentProfit={summary.totalProfit}
        />
        <InvestmentsFlowChart
          trends={summary.monthlyFlow}
        />
      </section>

      {/* Allocation + Indexer + Liquidity */}
      <section className={`grid auto-rows-fr grid-cols-1 items-stretch gap-3 ${hasUsefulIndexerData ? 'lg:grid-cols-3' : 'lg:grid-cols-2'}`}>
        <Card className="flex h-full flex-col overflow-hidden" data-testid="investment-allocation-card">
          <div className="border-b border-border-subtle p-4">
            <SectionHeading
              icon={<ChartPieSliceIcon size={14} className="text-primary" />}
              title="Alocação por Tipo"
            />
          </div>
          <div className="flex flex-1 flex-col p-4">
            {allocationChartData.length > 0 ? (
              <InvestmentAllocationChart
                data={allocationChartData}
                totalAmount={filteredTotals.totalBalance}
                selectedCategories={selectedCategories}
                isCategoryNegated={isCategoryNegated}
                onCategoryClick={handleCategorySelect}
              />
            ) : (
              <div className="flex min-h-24 flex-1 flex-col items-center justify-center text-center">
                <ChartPieSliceIcon size={24} className="mb-2 text-border-strong" />
                <p className="text-sm font-medium text-text-secondary">Sem dados</p>
              </div>
            )}
          </div>
        </Card>

        {hasUsefulIndexerData && (
          <IndexerConcentrationCard
            data={indexerData}
            total={fixedIncomeIndexers.total}
            selectedIndexer={selectedIndexer}
            onSelect={handleIndexerSelect}
          />
        )}

        <Card className="flex h-full flex-col p-4" data-testid="investment-liquidity-card">
          <div className="flex items-start justify-between gap-2">
            <SectionHeading
              icon={<DropIcon size={14} className="text-primary" />}
              title="Perfil de Liquidez"
            />
            <LiquidityMethodologyPopover />
          </div>
          {liquidityTotal > 0 ? (
            <>
              <div className="mt-1">
                <LiquidityBar
                  liquidityData={liquidityData}
                  total={liquidityTotal}
                  selectedLiquidity={selectedLiquidity}
                  onSelect={handleLiquiditySelect}
                  formatCurrency={formatCurrency}
                />
              </div>
              <div className="mt-4 space-y-1 sm:mt-3">
                {liquidityData.map(d => (
                  <LiquidityLegendRow
                    key={d.key}
                    datum={d}
                    active={selectedLiquidity === d.key}
                    dimmed={selectedLiquidity !== null && selectedLiquidity !== d.key}
                    pct={(d.value / liquidityTotal) * 100}
                    valueLabel={liquidityLensEnabled ? formatCurrency(d.value) : undefined}
                    onSelect={handleLiquiditySelect}
                  />
                ))}
              </div>
              <LiquiditySettlementNote count={summary.liquidity.settlementUnknownCount} />
            </>
          ) : (
            <p className="text-sm text-text-secondary sm:text-xs">Sem dados de liquidez.</p>
          )}
        </Card>
      </section>


      {/* Assets list */}
      <section>
        <div className="mb-3 flex items-center justify-between gap-2">
          <h2 className="text-sm font-semibold text-text-primary">Carteira de Ativos</h2>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleToggleWithdrawn}
              aria-pressed={includeWithdrawn}
              className={`rounded-lg px-2 py-1 text-2xs font-bold transition-colors ${
                includeWithdrawn
                  ? 'bg-brand-50 text-brand-700 ring-1 ring-border-brand'
                  : 'text-text-tertiary hover:bg-accent hover:text-text-secondary'
              }`}
            >
              Incluir resgatados
            </button>
            <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-2xs text-text-secondary tabular-nums">
              {positionsTotal}
            </span>
          </div>
        </div>

        {/* Institution rail (mockup "Trilho") — always rendered when the portfolio has
            any institution, one chip per connection, multi-select. */}
        {institutionFacets.length > 0 && (
          <div
            role="group"
            aria-label="Filtrar por instituição"
            className="scrollbar-hide mb-2 flex gap-1 overflow-x-auto pb-0.5"
          >
            <button
              type="button"
              onClick={handleInstitutionClearAll}
              aria-pressed={selectedInstitutions.size === 0}
              className={`flex h-9 shrink-0 items-center rounded-lg px-3 text-sm font-bold transition-colors sm:h-8 sm:text-xs ${
                selectedInstitutions.size === 0
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-surface-sunken text-text-secondary hover:bg-accent active:bg-accent'
              }`}
            >
              Todas
            </button>
            {institutionFacets.map((facet) => (
              <InstitutionChip
                key={facet.name}
                facet={facet}
                active={selectedInstitutions.has(facet.name)}
                onSelect={handleInstitutionSelect}
              />
            ))}
          </div>
        )}

        {/* Search & sort bar — prototype toolbar: search + sort chips only. All filters
            live on the page itself (institution rail, donut slices, indexer bars,
            liquidity rows); active selections surface as removable pills below. */}
        <SurfaceCard as="div" className={`mb-3 p-2 ${hasActiveInvestmentControls ? 'border-border-brand' : ''}`}>
          <div className="flex items-stretch gap-2">
            <div className="relative min-w-0 flex-1">
              <MagnifyingGlassIcon size={14} className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-text-tertiary" />
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Buscar ativo"
                className="h-11 w-full rounded-xl border border-border-subtle bg-surface-sunken pr-8 pl-9 text-base font-semibold text-text-primary placeholder:text-text-tertiary focus:border-border-strong focus:bg-surface focus:ring-2 focus:ring-primary/20 focus:outline-none sm:h-10 sm:text-sm"
              />
              {hasSearch && (
                <button
                  type="button"
                  onClick={handleSearchClear}
                  aria-label="Limpar busca"
                  className="absolute top-1/2 right-2.5 -translate-y-1/2 rounded-md p-1 text-text-tertiary active:text-error-text"
                >
                  <XIcon size={13} weight="bold" />
                </button>
              )}
            </div>
          </div>

          <SortChips
            ariaLabel="Ordenar investimentos"
            className="mt-2"
            options={[
              { value: 'balance', label: 'Saldo' },
              { value: 'profit', label: 'Lucro' },
              { value: 'name', label: 'Nome' },
            ]}
            value={sortField}
            order={sortOrder}
            onChange={handleSort}
          />

          {(selectedCategories.size > 0 || selectedInstitutions.size > 0 || selectedIndexer || selectedLiquidity) && (
            <div className="mt-2 flex flex-wrap items-center gap-2 px-1">
              {selectedCategories.size > 0 && (
                <button
                  type="button"
                  onClick={handleToggleFilterMode}
                  className={`text-2xs font-bold uppercase tracking-wide px-2 py-1 rounded transition-colors ${isCategoryNegated ? 'text-error-text bg-error-surface hover:bg-error-surface' : 'text-brand-700 bg-brand-50 hover:bg-brand-100'}`}
                >
                  {isCategoryNegated ? 'Excluindo' : 'Filtrando'}
                </button>
              )}
              {Array.from(selectedInstitutions).map(name => (
                <RemovableFilterPill
                  key={name}
                  value={name}
                  label={name}
                  imageUrl={institutionFacets.find(f => f.name === name)?.imageUrl ?? undefined}
                  removeLabel={`Remover filtro ${name}`}
                  onRemove={handleInstitutionSelect}
                />
              ))}
              {Array.from(selectedCategories).map(cat => (
                <RemovableFilterPill
                  key={cat}
                  value={cat}
                  label={ALLOCATION_CONFIG[cat as keyof typeof ALLOCATION_CONFIG]?.label ?? cat}
                  removeLabel={`Remover filtro ${ALLOCATION_CONFIG[cat as keyof typeof ALLOCATION_CONFIG]?.label ?? cat}`}
                  onRemove={handleCategorySelect}
                />
              ))}
              {selectedIndexer && (
                <RemovableFilterPill
                  value={selectedIndexer}
                  label={selectedIndexer === 'PRE' ? 'Prefixado' : selectedIndexer}
                  removeLabel="Remover filtro de indexador"
                  tone="brand"
                  onRemove={handleIndexerSelect}
                />
              )}
              {selectedLiquidity && (
                <RemovableFilterPill
                  value={selectedLiquidity}
                  label={liquidityFilterLabel(selectedLiquidity)}
                  removeLabel="Remover filtro de liquidez"
                  tone="brand"
                  onRemove={handleLiquiditySelect}
                />
              )}
              <button type="button" onClick={handleClearInvestmentFilters} className="px-1 text-2xs font-bold text-text-tertiary transition-colors hover:text-error">
                Limpar tudo
              </button>
            </div>
          )}
        </SurfaceCard>

        {/* Desktop table */}
        <div className={`hidden transition-opacity md:block ${isPositionsLoading ? 'opacity-60' : ''}`} aria-busy={isPositionsLoading}>
          <table className="w-full table-fixed border-collapse text-left">
            <thead>
              <tr className="border-y border-border-subtle text-2xs font-semibold text-text-tertiary">
                <th className="w-[26%] py-2.5 pr-4 pl-0 whitespace-nowrap">Ativo / emissor</th>
                <th className="hidden w-[13%] px-3 py-2.5 whitespace-nowrap lg:table-cell">Instituição</th>
                <th className="w-[12%] px-3 py-2.5 whitespace-nowrap">Tipo</th>
                <th className="hidden w-[14%] px-3 py-2.5 text-right whitespace-nowrap lg:table-cell">Total investido</th>
                <SortableHeader
                  field="balance"
                  label="Saldo atual"
                  widthClassName="w-[15%]"
                  sortField={sortField}
                  sortOrder={sortOrder}
                  onSort={handleSort}
                />
                <SortableHeader
                  field="profit"
                  label="Rentab."
                  widthClassName="w-[10%]"
                  sortField={sortField}
                  sortOrder={sortOrder}
                  onSort={handleSort}
                />
                <th className="w-[10%] py-2.5 pr-0 pl-3 text-right whitespace-nowrap">Ação</th>
              </tr>
            </thead>
            <tbody>
              {paginatedSearched.length > 0 ? (
                paginatedSearched.map((inv) => (
                  <InvestmentTableRow
                    key={inv.id}
                    inv={inv}
                    isFiltered={isFiltered}
                    formatCurrency={formatCurrency}
                    onOpenDetail={handleOpenDetail}
                    onOpenGrouping={handleOpenGrouping}
                  />
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="border-b border-border-subtle px-6 py-12 text-center text-sm text-text-secondary">
                    {searchQuery ? 'Nenhum ativo encontrado para a busca' : 'Nenhum ativo encontrado'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {positionsTotal > ITEMS_PER_PAGE && (
            <div className="flex items-center justify-between border-b border-border-subtle py-2.5">
              <p className="text-2xs font-medium text-text-secondary">
                {((currentPage - 1) * ITEMS_PER_PAGE) + 1}–{Math.min(currentPage * ITEMS_PER_PAGE, positionsTotal)} de {positionsTotal}
              </p>
              <div className="flex items-center gap-1">
                <button onClick={handlePrevPage} disabled={currentPage === 1} aria-label="Página anterior" className="rounded-lg p-2 transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-30">
                  <CaretLeftIcon size={16} className="text-text-secondary" />
                </button>
                {visiblePageNumbers.map((pageNum) => (
                  <PageNumberButton
                    key={pageNum}
                    page={pageNum}
                    isActive={currentPage === pageNum}
                    onSelect={handlePageChange}
                  />
                ))}
                <button onClick={handleNextPage} disabled={currentPage === searchedTotalPages} aria-label="Próxima página" className="rounded-lg p-2 transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-30">
                  <CaretRightIcon size={16} className="text-text-secondary" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Mobile card list */}
        <div className={`transition-opacity md:hidden ${isPositionsLoading ? 'opacity-60' : ''}`} aria-busy={isPositionsLoading}>
          {searchedInvestments.length > 0 ? (
            <div className="space-y-2">
              {paginatedSearched.map((inv) => (
                <InvestmentMobileCard
                  key={inv.id}
                  inv={inv}
                  formatCurrency={formatCurrency}
                  onOpenDetail={handleOpenDetail}
                  onOpenGrouping={handleOpenGrouping}
                />
              ))}
            </div>
          ) : (
            <Card className="p-6 text-center">
              <p className="text-base text-text-secondary sm:text-sm">
                {hasSearch ? 'Nenhum ativo encontrado para a busca' : 'Nenhum ativo encontrado'}
              </p>
            </Card>
          )}

          {positionsTotal > ITEMS_PER_PAGE && (
            <SurfaceCard as="div" className="mt-3 flex items-center justify-between px-3 py-2">
              <button
                type="button"
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className="flex size-10 items-center justify-center rounded-xl bg-surface-sunken text-text-secondary disabled:opacity-30"
                aria-label="Página anterior"
              >
                <CaretLeftIcon size={16} />
              </button>
              <span className="text-sm font-bold text-text-secondary">
                {currentPage} de {searchedTotalPages}
              </span>
              <button
                type="button"
                onClick={handleNextPage}
                disabled={currentPage === searchedTotalPages}
                className="flex size-10 items-center justify-center rounded-xl bg-surface-sunken text-text-secondary disabled:opacity-30"
                aria-label="Próxima página"
              >
                <CaretRightIcon size={16} />
              </button>
            </SurfaceCard>
          )}
        </div>

      </section>

      {/* Upcoming maturities + dynamics (responsive) */}
      <section className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <Card className="p-4 sm:p-5">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-text-primary">Próximos Vencimentos</h2>
          </div>
          {upcomingMaturities.length > 0 ? (
            <div className="divide-y divide-border-subtle">
              {upcomingMaturities.map((inv) => (
                <MaturityItem key={inv.id} inv={inv} formatCurrency={formatCurrency} />
              ))}
            </div>
          ) : (
            <p className="py-4 text-center text-sm text-text-secondary sm:text-xs">Sem vencimentos futuros</p>
          )}
          <Button variant="ghost" size="sm" onClick={handleOpenCalendar} className="mt-3 flex w-full items-center gap-1.5 text-brand-700 hover:bg-brand-50 hover:text-brand-700">
            <CalendarDotsIcon size={13} />
            Ver Calendário Completo
          </Button>
        </Card>

        {maturityByYear.length > 0 ? (
          <Card className="@container p-4 sm:p-5">
            <div className="mb-1 flex items-center gap-2">
              <TrendUpIcon size={15} className="text-primary" />
              <h2 className="text-sm font-semibold text-text-primary">Dinâmica de Renda Fixa</h2>
            </div>
            <p className="text-2xs text-text-tertiary">Vencimentos de renda fixa por ano</p>
            <FixedIncomeMaturityChart data={maturityByYear} formatCurrency={formatCurrency} />
          </Card>
        ) : (
          <Card className="flex items-center justify-center p-5">
            <p className="text-sm text-text-secondary sm:text-xs">Sem vencimentos futuros de renda fixa</p>
          </Card>
        )}
      </section>

      {/* Calendar sheet */}
      <Sheet open={calendarOpen} onOpenChange={setCalendarOpen}>
        <SheetContent
          side="right"
          className="w-full overflow-y-auto p-4 sm:max-w-3xl sm:p-6"
        >
          <SheetHeader>
            <SheetTitle className="sr-only">Calendário de Vencimentos</SheetTitle>
            <SheetDescription className="sr-only">
              Visualize os vencimentos dos seus investimentos por dia.
            </SheetDescription>
          </SheetHeader>
          <InvestmentCalendarView />
        </SheetContent>
      </Sheet>

      {/* Position quick-look drawer — the full /investments/:id page stays reachable
          from its footer (and by deep link). */}
      <InvestmentDetailDrawer
        investmentId={detailInvestmentId}
        groupId={detailRow?.groupId ?? null}
        sources={detailRow?.sources}
        onOpenChange={handleDetailDrawerOpenChange}
        onUnmerged={handlePortfolioChanged}
      />
      <InvestmentGroupPickerDrawer
        anchor={groupingAnchor}
        onClose={handleCloseGrouping}
        onGrouped={handlePortfolioChanged}
      />
    </div>
  );
}
